import { create } from 'zustand'
import produce from 'immer'
import { isBefore, parseISO, subDays as subtractDays } from 'date-fns'
import parsePhoneNumber from 'libphonenumber-js'

import { getDefaultPhoneNumber } from '../../utils/client-helpers'
import { createSelectors } from '../../utils/create-selector'

import type { Client } from '../../hooks/clients/queries'
import type { Template } from '../../hooks/templates/queries'
import type { UnlayerTemplate } from '../../unlayer/unlayer.schema'

type Account = {
  optinRequired?: boolean
}

type User = {
  hasTwilio: boolean
  hasSendGrid: boolean
}

type BroadcastFilters = {
  includeSms: boolean
  includeEmail: boolean
  includeRecentContacts: boolean
  includeSharedClients: boolean
  includeAssignedClients: boolean
  emailOnly: boolean
}

type BroadcastState = {
  initialized: boolean
  prefiltered: boolean
  account: Account | undefined
  user: User | undefined
  type: 'associate' | 'admin' | undefined
  smsClientList: Array<Client>
  emailClientList: Array<Client>
  template: Template | null
  htmlEmail: UnlayerTemplate | null
  callListId: string | null
  callListDynamicId: string | null
} & BroadcastFilters

type BroadcastActions = {
  initializeBroadcast: ({
    account,
    user,
    type,
    clients,
    template,
    callListId,
    callListDynamicId,
  }: {
    account: Account
    user: User
    type: 'associate' | 'admin'
    clients?: Array<Client>
    template?: Template | null
    callListId?: string | null
    callListDynamicId?: string | null
  }) => void
  updateClientList: (clients: Array<Client>) => void
  setFilter: <K extends keyof BroadcastFilters>(
    filter: K,
    val: BroadcastFilters[K]
  ) => void
  setHtmlEmail: ({ json, html, img }: UnlayerTemplate) => void
  removeClient: (clientId: string) => void
  reset: () => void
  resetHtmlEmail: () => void
}

type BroadcastStore = BroadcastState & { actions: BroadcastActions }

const defaultState = () =>
  ({
    initialized: false,
    prefiltered: false,
    account: undefined,
    user: undefined,
    type: undefined,
    smsClientList: [],
    emailClientList: [],
    includeSms: true,
    includeEmail: true,
    emailOnly: false,
    includeRecentContacts: false,
    includeAssignedClients: false,
    includeSharedClients: false,
    template: null,
    htmlEmail: null,
    callListId: null,
    callListDynamicId: null,
  } as BroadcastState)

function hasValidPhoneNumber(phoneNumbers: Client['phoneNumbers']) {
  const phoneNumber = getDefaultPhoneNumber(phoneNumbers)

  return (
    phoneNumber &&
    phoneNumber.e164 &&
    parsePhoneNumber(phoneNumber.e164.toString(), 'US')?.isValid() &&
    !phoneNumber.isLandLine
  )
}

// look into factoring out user to clean up the logic
const useBroadcastBase = create<BroadcastStore>()((set, get) => ({
  ...defaultState(),
  actions: {
    setFilter: (filter, val) =>
      set(
        produce((state: BroadcastStore) => {
          state[filter] = val
        })
      ),
    initializeBroadcast: ({
      clients,
      account,
      user,
      type,
      template,
      callListId,
      callListDynamicId,
    }) => {
      set(
        produce((state: BroadcastStore) => {
          // should only need to check for allowTwilioSms
          // but since associates are able to broadcast from any list
          // we cannot rely on the new server api returning only subscribers... So still have to do below

          state.account = account
          state.user = user
          state.type = type
          state.includeSharedClients = type === 'admin' ? true : false
          state.includeAssignedClients = type === 'admin' ? true : false
          state.initialized = true
          state.prefiltered = clients ? clients.length > 0 : false
          if (template) state.template = template
          if (callListId) state.callListId = callListId
          if (callListDynamicId) state.callListDynamicId = callListDynamicId
        })
      )
      // update/reset list only if not initialized
      // this handles the cases where user navigates away via menu thus not unmounting the create component (which means its useEffect won't run)
      if ((!get().initialized && clients) || (get().prefiltered && clients)) {
        get().actions.updateClientList(clients)
      }
    },
    setHtmlEmail: ({ json, html, img }) => {
      set(
        produce((state: BroadcastState) => {
          state.htmlEmail = { json, html, img }
        })
      )
    },
    updateClientList: (clients) => {
      set(
        produce((state: BroadcastStore) => {
          const validSmsClients = clients.filter((client) => {
            const validSMSSendAccount =
              state.account?.optinRequired && state.user?.hasTwilio

            const unsubscribed =
              client.unsubscribedAt || client.posActive === false
            const validPhone = hasValidPhoneNumber(client.phoneNumbers)

            const optedIn =
              !state.account?.optinRequired ||
              getDefaultPhoneNumber(client.phoneNumbers)?.optInState ===
                'OPTED_IN'

            return validSMSSendAccount && !unsubscribed && validPhone && optedIn
          })

          const validEmailClients = clients.filter((client) => {
            const validEmailSendAccount = state.user?.hasSendGrid
            const emailOnly = state.emailOnly
              ? !hasValidPhoneNumber(client.phoneNumbers)
              : true

            const unsubscribed =
              client.unsubscribedAt || client.posActive === false
            const inSms = validSmsClients.some(
              (smsClient) => client.objectId === smsClient.objectId
            )

            return (
              validEmailSendAccount &&
              emailOnly &&
              !unsubscribed &&
              !inSms &&
              Boolean(client.email)
            )
          })
          state.smsClientList = validSmsClients
          state.emailClientList = validEmailClients
        })
      )
    },

    reset: () => set(defaultState()),
    resetHtmlEmail: () =>
      set(
        produce((state: BroadcastState) => {
          state.htmlEmail = null
        })
      ),
    removeClient: (clientId: string) =>
      set(
        produce((state: BroadcastStore) => {
          state.smsClientList = state.smsClientList.filter(
            (client) => client.objectId !== clientId
          )

          state.emailClientList = state.emailClientList.filter(
            (client) => client.objectId !== clientId
          )
        })
      ),
  },
}))

export const selectors = {
  broadcastState: (state: BroadcastStore) => ({
    initialized: state.initialized,
    prefiltered: state.prefiltered,
    type: state.type,
  }),
  filteredSmsList: (state: BroadcastStore) => {
    let temp = state.type === 'admin' ? [] : state.smsClientList

    if (state.type === 'admin') {
      temp = [
        ...(state.includeSharedClients
          ? state.smsClientList.filter((client) => !Boolean(client.users))
          : []),
        ...(state.includeAssignedClients
          ? state.smsClientList.filter((client) => Boolean(client.users))
          : []),
      ]
    }
    return [...(state.includeSms ? temp : [])].filter((client) =>
      state.includeRecentContacts
        ? true
        : !client.lastContact ||
          isBefore(
            parseISO(client.lastContact.iso),
            subtractDays(new Date(), 1)
          )
    )
  },
  filteredEmailList: (state: BroadcastStore) => {
    let temp = state.type === 'admin' ? [] : state.emailClientList

    if (state.type === 'admin') {
      temp = [
        ...(state.includeSharedClients
          ? state.emailClientList.filter((client) => !Boolean(client.users))
          : []),
        ...(state.includeAssignedClients
          ? state.emailClientList.filter((client) => Boolean(client.users))
          : []),
      ]
    }

    return [...(state.includeEmail ? temp : [])].filter((client) =>
      state.includeRecentContacts
        ? true
        : !client.lastContact ||
          isBefore(
            parseISO(client.lastContact.iso),
            subtractDays(new Date(), 1)
          )
    )
  },
  clientList: (state: BroadcastStore) => [
    ...selectors.filteredSmsList(state),
    ...selectors.filteredEmailList(state),
  ],
}

const useBroadcast = createSelectors(useBroadcastBase)

const useBroadcastActions = () => useBroadcastBase((state) => state.actions)

export { useBroadcast, useBroadcastActions }
